import React from "react"
import {Zap, Wrench, Radio, Truck} from 'lucide-react'

const PortablePage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Remote Start Portable Generator Kits & Guides
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Add wireless remote start capability to portable generators that didn't come with factory automation. Perfect for RV, camping, and emergency backup applications.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Add Remote Start to Your Portable Generator?
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Many quality portable generators like Honda EU series, Predator, and Westinghouse models come with pull-start or manual electric start only. Adding a remote start kit gives you the convenience of starting your generator from inside your RV, home, or vehicle without going outside in bad weather or at night.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Remote start kits are especially valuable for RV owners who need to start their generator while driving, or for home backup systems where the generator is located away from the house.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Benefits of Remote Start Systems
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Radio className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wireless Control</h3>
              <p className="text-gray-700">
                Start and stop your generator from up to 300 feet away with a key fob remote.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Truck className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">RV Convenience</h3>
              <p className="text-gray-700">
                Start your generator while driving to pre-cool or pre-heat your RV before stops.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Weather Protection</h3>
              <p className="text-gray-700">
                No need to go outside in rain, snow, or extreme temperatures to start your generator.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Wrench className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">DIY Friendly</h3>
              <p className="text-gray-700">
                Most kits can be installed in 1-2 hours with basic tools and mechanical knowledge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compatible Generators */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Compatible Portable Generator Brands
            </h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Honda Generators</h3>
                <p className="text-gray-700 mb-2">
                  EU2200i, EU3000is, EU7000is and other inverter models. These generators are known for quiet operation and reliability, making them ideal for RV and home backup use.
                </p>
                <p className="text-sm text-gray-600">
                  Installation Note: Honda generators with electric start are easiest to retrofit with remote start capability.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Predator (Harbor Freight)</h3>
                <p className="text-gray-700 mb-2">
                  Predator 3500, 9000, and other inverter series. Excellent value generators that work well with aftermarket remote start kits.
                </p>
                <p className="text-sm text-gray-600">
                  Installation Note: Compatible with most universal remote start modules.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Westinghouse</h3>
                <p className="text-gray-700 mb-2">
                  iGen series and other inverter models. Quality generators with competitive pricing and good support for modifications.
                </p>
                <p className="text-sm text-gray-600">
                  Installation Note: Electric start models recommended for easiest installation.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Champion, Generac, and Others</h3>
                <p className="text-gray-700">
                  Most portable generators with electric start capability can be retrofitted with remote start systems. Check specifications for 12V starter systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Basic Installation Process
            </h2>
            
            <div className="space-y-4">
              {[
                "Verify your generator has an electric start (12V starter) - remote start requires this",
                "Mount the remote start module in a weather-protected location on or near the generator",
                "Connect the module to the generator's starter circuit and ignition system",
                "Install the wireless receiver antenna in a location with clear line of sight",
                "Program the key fob remote and test start/stop functions",
                "Secure all wiring and ensure proper strain relief for portable applications"
              ].map((step, index) => (
                <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 pt-1">{step}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Important Safety Note</h4>
              <p className="text-gray-700">
                Always disconnect the spark plug before working on generator electrical systems. Remote start systems should include safety features like automatic shutdown on low oil pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Add Remote Start?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Check out our detailed installation guides for step-by-step instructions specific to your generator brand
          </p>
        </div>
      </section>
    </div>
  )
}

export default PortablePage
